// Signup funnel tracking helpers.
//
// The free-trial funnel is three explicit Amplitude events:
//   pilot_page_viewed  → the marketing pilot landing page (deliverables.ai/pilot) mounted
//   signup_started     → the user actually submitted account creation (here)
//   signup_completed   → the server created a fresh org + owner (auth-context,
//                        keyed off BootstrapResponse.created)
//
// signup_started fires on /login (or wherever account creation is initiated),
// but signup_completed fires later, from auth-context, AFTER the bootstrap call
// resolves — across an email-verification round trip for email/password signups.
// To carry attribution (which source/method started this signup) across that
// gap we stash a small intent in localStorage (survives a same-origin
// new-tab return from the verification link, unlike sessionStorage) and consume
// it once at completion. A max-age guard keeps a stale intent from
// mis-attributing a much-later signup.
//
// Google Ads click ids (gclid / wbraid / gbraid) ride the same intent so the
// backend can upload offline conversions against them (ENG-5227 / ENG-2919).
// The ad click lands on the marketing site and the signup happens here later,
// so the ids are also stashed on FIRST TOUCH of any app page and kept for 90
// days (Google's maximum click-through conversion window); a signup whose URL
// carries no click id falls back to that stash.

"use client";

import { track } from "./analytics";

const KEY = "da_signup_intent";
const PENDING_KEY = "da_signup_pending";
const CLICK_ID_KEY = "da_click_ids";
const FIRST_TOUCH_KEY = "da_first_touch";
const MAX_AGE_MS = 2 * 60 * 60 * 1000; // 2h — long enough for the verify hop
const CLICK_ID_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000; // Google's longest click window
const MAX_ATTRIBUTION_LENGTH = 512;
const CAMPAIGN_PARAM_KEYS = [
  "src",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "utm_id",
  "utm_source_platform",
  "utm_creative_format",
  "utm_marketing_tactic",
  // Website attribution (ENG-5879): the button (`cta`) and the deliverables.ai
  // pages (`site_page`, `site_landing`) the visitor came through.
  "cta",
  "site_page",
  "site_landing",
] as const;
// Google auto-tagging appends exactly one of these to the landing URL.
const CLICK_ID_PARAM_KEYS = ["gclid", "wbraid", "gbraid"] as const;
const ATTRIBUTION_PARAM_KEYS = [...CAMPAIGN_PARAM_KEYS, ...CLICK_ID_PARAM_KEYS] as const;
const ATTRIBUTION_KEYS = new Set<string>([
  "source",
  ...ATTRIBUTION_PARAM_KEYS,
  "referrer",
  "landing_path",
]);
// Website page paths: like `landing_path`, they must be a path ("/..."), so a
// full URL or free text is dropped rather than stored.
const PATH_KEY_SET = new Set<string>(["landing_path", "site_page", "site_landing"]);
const CLICK_ID_KEY_SET = new Set<string>(CLICK_ID_PARAM_KEYS);
// A click id is an opaque URL-safe token. Anything else (a URL, whitespace, a
// percent-escape) is not one, and the value is forwarded verbatim to Google's
// conversion upload, so the shape check is stricter than the generic scrub.
const CLICK_ID_RE = /^[A-Za-z0-9._~-]{1,512}$/;
const SECRET_PATH_RE =
  /\/(api\/share|api\/invites|api\/workspace-invites|api\/org\/invites|share|invite|workspace-invite)\/[^/?#\s"'()[\]]+/gi;
const WORKSPACE_INVITE_PATH_RE =
  /(\/api\/workspaces\/[^/]+\/invites)\/[^/?#\s"'()[\]]+/gi;
const SECRET_QUERY_RE =
  /([?&#](?:oobCode|apiKey|token|access_token|id_token)=)[^&#\s"'()[\]]+/gi;
const HTTP_USERINFO_RE = /(https?:\/\/)[^/?#\s]*@/gi;

export type SignupMethod = "email" | "google";
export type SignupAttribution = Record<string, string> & { source: string };

type SignupIntent = {
  source: string;
  method: SignupMethod;
  ts: number;
  attribution: SignupAttribution;
};

/** Scrub a URL-borne value the same way signup attribution is scrubbed
 * (credentials, invite and share tokens, Firebase codes), capped at `max`
 * code points. Shared with the composer deep link (ENG-5231). Attribution
 * values are percent-decoded first so an encoded credential cannot slip
 * through; pass `decode: false` for text that `URLSearchParams` has already
 * decoded once and that the user will see verbatim (a prompt), so a literal
 * `%2F` in it is not decoded a second time. */
export function scrubUntrustedValue(
  value: string,
  max: number = MAX_ATTRIBUTION_LENGTH,
  { decode = true }: { decode?: boolean } = {},
): string {
  return sanitizeAttributionValue(value, max, decode);
}

function sanitizeAttributionValue(
  value: string,
  max: number = MAX_ATTRIBUTION_LENGTH,
  decode: boolean = true,
): string {
  // Strip through the last `@` in the authority before decoding so encoded
  // username/password delimiters cannot be turned into persisted path text.
  let decoded = value.replace(HTTP_USERINFO_RE, "$1");
  if (decode) {
    try {
      decoded = decodeURIComponent(decoded);
    } catch {
      // A malformed escape is still safe to process as literal text.
    }
  }
  const sanitized = decoded
    .replace(HTTP_USERINFO_RE, "$1")
    .replace(SECRET_PATH_RE, "/$1/[redacted]")
    .replace(WORKSPACE_INVITE_PATH_RE, "$1/[redacted]")
    .replace(SECRET_QUERY_RE, "$1[redacted]");
  return Array.from(sanitized).slice(0, max).join("");
}

function referrerOrigin(value: string): string | null {
  try {
    const url = new URL(value);
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;
    return sanitizeAttributionValue(url.origin);
  } catch {
    return null;
  }
}

function clickIdValue(value: string): string | null {
  return CLICK_ID_RE.test(value) ? value : null;
}

/** Per-key sanitiser: referrer keeps its origin only, click ids must be
 * token-shaped, everything else gets the generic scrub. */
function sanitizeAttributionField(key: string, value: string): string | null {
  if (key === "referrer") return referrerOrigin(value);
  if (PATH_KEY_SET.has(key)) return value.startsWith("/") ? sanitizeAttributionValue(value) : null;
  if (CLICK_ID_KEY_SET.has(key)) return clickIdValue(value);
  return sanitizeAttributionValue(value) || null;
}

type ClickIds = Partial<Record<(typeof CLICK_ID_PARAM_KEYS)[number], string>>;

function clickIdsFrom(params: URLSearchParams): ClickIds {
  const ids: ClickIds = {};
  for (const key of CLICK_ID_PARAM_KEYS) {
    const value = params.get(key);
    const clean = value ? clickIdValue(value) : null;
    if (clean) ids[key] = clean;
  }
  return ids;
}

function loadClickIds(): ClickIds | null {
  try {
    const raw = window.localStorage.getItem(CLICK_ID_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as { ids?: unknown; ts?: unknown } | null;
    const now = Date.now();
    if (
      !value ||
      typeof value !== "object" ||
      typeof value.ts !== "number" ||
      !Number.isFinite(value.ts) ||
      value.ts > now ||
      now - value.ts > CLICK_ID_MAX_AGE_MS ||
      !value.ids ||
      typeof value.ids !== "object" ||
      Array.isArray(value.ids)
    ) {
      clearClickIds();
      return null;
    }
    const ids: ClickIds = {};
    for (const key of CLICK_ID_PARAM_KEYS) {
      const stored = (value.ids as Record<string, unknown>)[key];
      const clean = typeof stored === "string" ? clickIdValue(stored) : null;
      if (clean) ids[key] = clean;
    }
    if (Object.keys(ids).length === 0) {
      clearClickIds();
      return null;
    }
    return ids;
  } catch {
    return null;
  }
}

/**
 * Stash any Google Ads click id on the landing URL for 90 days. Call once per
 * page load (Providers mount): the ad click lands on the marketing site and
 * arrives here on a link that forwards the id, often days before the signup.
 * A newer click replaces an older stash (last-click attribution, matching how
 * Google credits the conversion); a URL with no click id leaves it alone.
 */
export function rememberClickIds() {
  if (typeof window === "undefined") return;
  const ids = clickIdsFrom(signupAttributionParams());
  if (Object.keys(ids).length === 0) return;
  try {
    // A reload or a bookmarked landing URL carries the SAME click again; that
    // is not a new click, so it keeps its original capture time and still ages
    // out at 90 days. Only a distinct id restarts the clock.
    const ts = storedClickIdsTimestamp(ids) ?? Date.now();
    window.localStorage.setItem(CLICK_ID_KEY, JSON.stringify({ ids, ts }));
  } catch {
    /* private mode / storage disabled — the signup URL may still carry it */
  }
}

/** The stash's capture time when it holds exactly `ids`, else null. Reads the
 * raw record rather than `loadClickIds()` so an expired stash of the same
 * click is not revived with a fresh timestamp. */
function storedClickIdsTimestamp(ids: ClickIds): number | null {
  try {
    const raw = window.localStorage.getItem(CLICK_ID_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as { ids?: unknown; ts?: unknown } | null;
    if (!value || typeof value.ts !== "number" || !Number.isFinite(value.ts)) return null;
    if (value.ts > Date.now()) return null;
    const stored = value.ids;
    if (!stored || typeof stored !== "object" || Array.isArray(stored)) return null;
    const same = CLICK_ID_PARAM_KEYS.every(
      (key) => (stored as Record<string, unknown>)[key] === ids[key],
    );
    return same ? value.ts : null;
  } catch {
    return null;
  }
}

/** Drop the first-touch click ids. Called on sign-out, so a later signup by
 * someone else in this browser can't inherit a click that never belonged to
 * them. */
export function clearClickIds() {
  try {
    window.localStorage.removeItem(CLICK_ID_KEY);
  } catch {
    /* ignore */
  }
}

/** Retire the stash once a signup has consumed exactly the ids it holds: the
 * click has converted, so a second signup here must not be credited to it. A
 * stash holding a DIFFERENT click (one that landed after signup started, in
 * the verification window or another tab) is the newer click and stays. */
function retireConsumedClickIds(consumed: SignupAttribution) {
  const ids: ClickIds = {};
  for (const key of CLICK_ID_PARAM_KEYS) {
    if (consumed[key]) ids[key] = consumed[key];
  }
  if (Object.keys(ids).length === 0) return;
  if (storedClickIdsTimestamp(ids) !== null) clearClickIds();
}

type FirstTouch = { referrer?: string; landing_path?: string };
type CampaignFields = Partial<Record<(typeof CAMPAIGN_PARAM_KEYS)[number], string>>;

/** Stash where this browser first arrived (external referrer origin + path),
 * once, so a signup that starts later from /login still reports it (ENG-5736).
 * Never overwritten; cleared on sign-out with the click ids. */
export function rememberFirstTouch() {
  if (typeof window === "undefined") return;
  try {
    if (window.localStorage.getItem(FIRST_TOUCH_KEY)) return;
    const touch: FirstTouch = {};
    const referrer = typeof document === "undefined" ? null : referrerOrigin(document.referrer);
    // Same-origin navigations are not an arrival from elsewhere.
    if (referrer && referrer !== window.location.origin) touch.referrer = referrer;
    const path = sanitizeAttributionField("landing_path", window.location.pathname || "");
    if (path) touch.landing_path = path;
    // The landing URL's campaign fields too, for a signup with no intent
    // (codex P1 on #3025). Kept apart from `loadFirstTouch`, which feeds
    // captureSignupAttribution, so they never override the signup URL's own.
    const params = new URLSearchParams(window.location.search);
    const campaign: CampaignFields = {};
    for (const key of CAMPAIGN_PARAM_KEYS) {
      const value = params.get(key);
      const clean = value ? sanitizeAttributionField(key, value) : null;
      if (clean) campaign[key] = clean;
    }
    window.localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify({ ...touch, campaign }));
  } catch {
    /* storage disabled — the signup URL may still carry its own */
  }
}

export function loadFirstTouch(): FirstTouch | null {
  try {
    const value = JSON.parse(window.localStorage.getItem(FIRST_TOUCH_KEY) ?? "null");
    if (!value || typeof value !== "object" || Array.isArray(value)) return null;
    const out: FirstTouch = {};
    for (const key of ["referrer", "landing_path"] as const) {
      const clean = typeof value[key] === "string" ? sanitizeAttributionField(key, value[key]) : null;
      if (clean) out[key] = clean;
    }
    return Object.keys(out).length > 0 ? out : null;
  } catch {
    return null;
  }
}

/** Attribution for a bootstrap with no signup intent (a Google sign-in from
 * the default sign-in mode that created the account): the first touch, its
 * landing campaign fields and any stashed Google Ads click id, so the server
 * records the real source and the Ads uploader keeps its click id (ENG-5736). */
export function noIntentAttribution(): SignupAttribution | null {
  const out: Record<string, string> = {};
  try {
    const value = JSON.parse(window.localStorage.getItem(FIRST_TOUCH_KEY) ?? "null");
    const campaign = value && typeof value === "object" ? value.campaign : null;
    if (campaign && typeof campaign === "object" && !Array.isArray(campaign)) {
      for (const key of CAMPAIGN_PARAM_KEYS) {
        const raw = campaign[key];
        const clean = typeof raw === "string" ? sanitizeAttributionField(key, raw) : null;
        if (clean) out[key] = clean;
      }
    }
  } catch {
    /* storage disabled or corrupt — fall through to what else is available */
  }
  // The current URL's campaign fields beat a stale first touch (codex P2 on
  // #3025): a later arrival at /login?src=pilot is the campaign that converted.
  const params = signupAttributionParams();
  for (const key of CAMPAIGN_PARAM_KEYS) {
    const value = params.get(key);
    const clean = value ? sanitizeAttributionField(key, value) : null;
    if (clean) out[key] = clean;
  }
  Object.assign(out, loadFirstTouch() ?? {}, loadClickIds() ?? {});
  return Object.keys(out).length > 0 ? (out as SignupAttribution) : null;
}

export function clearFirstTouch() {
  try {
    window.localStorage.removeItem(FIRST_TOUCH_KEY);
  } catch {
    /* ignore */
  }
}

function signupAttributionParams(): URLSearchParams {
  if (typeof window === "undefined") return new URLSearchParams();
  const params = new URLSearchParams(window.location.search);
  if (ATTRIBUTION_PARAM_KEYS.some((key) => params.get(key))) return params;
  const next = params.get("next");
  if (!next) return params;
  try {
    const destination = new URL(next, window.location.origin);
    return destination.origin === window.location.origin ? destination.searchParams : params;
  } catch {
    return params;
  }
}

/** Capture the allowlisted campaign fields available at the signup action. */
function captureSignupAttribution(): SignupAttribution {
  const attribution: Record<string, string> = {};
  const params = signupAttributionParams();
  for (const key of ATTRIBUTION_PARAM_KEYS) {
    const value = params.get(key);
    const clean = value ? sanitizeAttributionField(key, value) : null;
    if (clean) attribution[key] = clean;
  }
  // No click id on the signup URL: fall back to the first-touch stash. The URL's
  // own ids win as a set (they are the newer click).
  if (!CLICK_ID_PARAM_KEYS.some((key) => attribution[key])) {
    Object.assign(attribution, loadClickIds() ?? {});
  }
  // Auto-tagging sets a click id without any utm_*, so an ad signup with no
  // explicit campaign source is still attributed to Google Ads in the funnel.
  const fallbackSource = CLICK_ID_PARAM_KEYS.some((key) => attribution[key])
    ? "google_ads"
    : "login";
  attribution.source = sanitizeAttributionValue(
    params.get("src") || params.get("utm_source") || fallbackSource,
  );
  if (typeof document !== "undefined") {
    const referrer = referrerOrigin(document.referrer);
    if (referrer) attribution.referrer = referrer;
  }
  // First touch wins over the (usually same-site) page the signup started on.
  Object.assign(attribution, loadFirstTouch() ?? {});
  return attribution as SignupAttribution;
}

function normalizeAttribution(value: unknown, source: string): SignupAttribution | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const normalized: Record<string, string> = { source };
  for (const [key, raw] of Object.entries(value)) {
    if (!ATTRIBUTION_KEYS.has(key)) continue;
    if (typeof raw !== "string") return null;
    const clean = sanitizeAttributionField(key, raw);
    if (clean) normalized[key] = clean;
  }
  normalized.source = source;
  return normalized as SignupAttribution;
}

function loadSignupIntent(): SignupIntent | null {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as Record<string, unknown> | null;
    const now = Date.now();
    if (
      !value ||
      typeof value !== "object" ||
      typeof value.source !== "string" ||
      (value.method !== "email" && value.method !== "google") ||
      typeof value.ts !== "number" ||
      !Number.isFinite(value.ts) ||
      value.ts > now ||
      now - value.ts > MAX_AGE_MS
    ) {
      clearSignupIntent();
      return null;
    }
    const source = sanitizeAttributionValue(value.source);
    if (!source) {
      clearSignupIntent();
      return null;
    }
    // Accept the pre-attribution intent shape during a rolling web deploy.
    const attribution = normalizeAttribution(value.attribution ?? { source }, source);
    if (!attribution) {
      clearSignupIntent();
      return null;
    }
    return { source, method: value.method, ts: value.ts, attribution };
  } catch {
    return null;
  }
}

/**
 * Record that the user kicked off account creation, and fire `signup_started`.
 * `source` attributes where the signup originated (e.g. "pilot", "login").
 * The intent is persisted so `consumeSignupIntent` can attach the same
 * source/method to the later `signup_completed` event.
 */
export function markSignupStarted(method: SignupMethod) {
  const attribution = captureSignupAttribution();
  const source = attribution.source;
  try {
    const intent: SignupIntent = { source, method, ts: Date.now(), attribution };
    window.localStorage.setItem(KEY, JSON.stringify(intent));
  } catch {
    /* private mode / storage disabled — the event below still fires */
  }
  track("signup_started", { method, source, ...ctaProp(attribution) });
}

function ctaProp(attribution: Record<string, string> | null | undefined): { cta?: string } {
  const cta = attribution?.cta;
  return cta ? { cta } : {};
}

/** The `cta` to put on `signup_completed`, from the intent stashed at start. */
export function signupCtaProp(intent: SignupIntent | null): { cta?: string } {
  return ctaProp(intent?.attribution);
}

export const SIGNUP_METHODS_AVAILABLE: readonly SignupMethod[] = ["email", "google"];

/** `signup_page_viewed`: the login page showed the signup form. `source` is the
 * same src/utm fallback `signup_started` reports; the caller latches it to one
 * fire per page load. */
export function trackSignupPageViewed() {
  const attribution = captureSignupAttribution();
  track("signup_page_viewed", {
    source: attribution.source,
    ...ctaProp(attribution),
    method_available: [...SIGNUP_METHODS_AVAILABLE],
  });
}

export type SignupErrorCode =
  | "email_in_use"
  | "weak_password"
  | "invalid_email"
  | "popup_closed"
  | "network"
  | "verification_pending"
  | "too_many_requests"
  | "other";

const SIGNUP_ERROR_CODES: Record<string, SignupErrorCode> = {
  "auth/email-already-in-use": "email_in_use",
  "auth/weak-password": "weak_password",
  "auth/invalid-email": "invalid_email",
  "auth/popup-closed-by-user": "popup_closed",
  "auth/cancelled-popup-request": "popup_closed",
  "auth/network-request-failed": "network",
  "auth/email-not-verified": "verification_pending",
  "auth/too-many-requests": "too_many_requests",
};

/** Map a failed signup's error to the fixed `error_code` enum. Reads only the
 * Firebase `code`; the message and everything else are never returned, so an
 * email address or free text cannot reach the event. Unknown becomes `other`. */
export function signupErrorCode(err: unknown): SignupErrorCode {
  const code = (err as { code?: unknown } | null)?.code;
  return typeof code === "string" && Object.hasOwn(SIGNUP_ERROR_CODES, code)
    ? SIGNUP_ERROR_CODES[code]
    : "other";
}

/** `signup_failed`: an account-creation attempt did not succeed. */
export function trackSignupFailed(method: SignupMethod, err: unknown) {
  track("signup_failed", { method, error_code: signupErrorCode(err) });
}

/**
 * Mark that a genuine new account was created (bootstrap `created`), so
 * `signup_completed` is guaranteed to fire once even if the page reloads before
 * it does. The completion latch can't live only in a React ref: the post-signup
 * `/api/me` can hit a stale-workspace 401 that makes apiFetch reload the page
 * (dropping the ref), or a transient retry can land — both lose an in-memory
 * latch. Persisting here survives those. Set on `created` and consumed once a
 * successful account load fires the event. `funded` carries the trial-grant
 * result so the event reports it even across a reload. Keyed by `uid` so a
 * different account loading in the same browser within the window can't consume
 * another user's completion (which would attribute signup_completed to the wrong
 * user).
 */
export function markSignupPending(uid: string, funded: boolean) {
  try {
    window.localStorage.setItem(PENDING_KEY, JSON.stringify({ uid, funded, ts: Date.now() }));
  } catch {
    /* storage disabled — falls back to no completion event */
  }
}

/** Read and clear the persisted completion marker IFF it belongs to `uid` and is
 * recent. Returns the captured `funded` flag, or null when absent/stale/another
 * user's (a mismatched marker is left in place so its real owner can still
 * consume it before it expires). */
export function consumeSignupPending(uid: string): { funded: boolean } | null {
  try {
    const raw = window.localStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as { uid?: unknown; funded?: unknown; ts?: unknown };
    if (typeof p.ts !== "number" || Date.now() - p.ts > MAX_AGE_MS) {
      window.localStorage.removeItem(PENDING_KEY); // stale — drop it
      return null;
    }
    if (p.uid !== uid || typeof p.funded !== "boolean") return null; // not ours — leave it
    window.localStorage.removeItem(PENDING_KEY);
    return { funded: p.funded };
  } catch {
    return null;
  }
}

/** Drop any stored signup intent. Call on non-signup auth (a sign-in, or
 * sign-out) so a leftover intent from an abandoned signup can't attribute the
 * NEXT user's signup_completed to the wrong source/method. */
export function clearSignupIntent() {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

/** Read a valid intent without consuming it so bootstrap retries reuse it. */
export function readSignupIntent(): SignupIntent | null {
  const intent = loadSignupIntent();
  return intent ? { ...intent, attribution: { ...intent.attribution } } : null;
}

export function consumeSignupIntent(): SignupIntent | null {
  const intent = loadSignupIntent();
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* storage disabled — return the validated in-memory value */
  }
  if (intent) retireConsumedClickIds(intent.attribution);
  return intent;
}
